import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type Stripe from "stripe";
import styled from "styled-components";

import { WorkspacePlanTier } from "common/interfaces";
import stripeProducts, { RecurringPlanDefinition } from "common/stripeProducts";
import Analytics from "js/analytics";
import { Button } from "js/Components/Button";
import { getStaticUrl } from "js/config";
import BillingController, { BillingControllerState, upgradeWorkspacePlan } from "js/controllers/BillingController";
import WorkspaceController, { WorkspaceControllerState, setPersistentWorkspaceId } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { app } from "js/namespaces";
import { ShowDialog, ShowDialogAsync, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import BeautifulDialog from "js/react/components/Dialogs/BeautifulDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { themeColors } from "js/react/sharedStyles";
import { TermsOfUseAndRefundPolicy } from "js/react/views/Billing/Checkout/components/TermsOfUseAndRefundPolicy/TermsOfUseAndRefundPolicy";
import { UpgradeFailedError } from "js/react/views/Billing/Checkout/plans/team/TeamCheckout";
import SuccessfulPurchaseDialog from "js/react/views/Billing/dialogs/SuccessfulPurchaseDialog";
import { defaultText, settingsLabel } from "js/textStyles";

import ChangePaymentMethodDialog from "./ChangePaymentMethodDialog";
import { PaymentMethodSection } from "./components/PaymentMethodSection";
import { SummarySection } from "./components/SummarySection";
import * as StripeUtils from "common/utils/stripeUtils";

const StyledDialogTitle = styled(DialogTitle)`
    h2 {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 20px;
    }
`;

const StyledDialogActions = styled(DialogActions)`
    &&& {
        justify-content: space-between;
    }
`;

const SectionTitle = styled.div`
    ${settingsLabel}
    color: #333;
    font-weight: 600;
    text-transform: uppercase;
`;

const SectionContent = styled.div`
    ${defaultText}
    color: ${themeColors.mediumGray};
`;

const SmallText = styled.span`
    font-size: 12px;
`;

type UpgradeToProWorkspaceDialogProps = WorkspaceControllerState & BillingControllerState & {
    closeDialog: (result: Boolean) => void;
    onUpgradeStart?: () => void;
}

const UpgradeToProWorkspaceDialog = (props: UpgradeToProWorkspaceDialogProps) => {
    const {
        workspace: { id: workspaceId },
        stripeData: { subscription, customer },
        closeDialog,
        onUpgradeStart
    } = props;

    const { t } = useTranslation();
    const [billingInterval, setBillingInterval] = useState<"month" | "year">("year");
    const [paymentMethod, setPaymentMethod] = useState<Stripe.PaymentMethod | null>(BillingController?.paymentMethod ?? null);
    const taxIdValue = StripeUtils.Customer.getTaxId(customer);
    const taxIdType = StripeUtils.Customer.getTaxIdType(customer);
    const billingAddress = StripeUtils.Customer.getBillingAddress(customer, paymentMethod);

    const tier = WorkspacePlanTier.PRO;
    const productDefinition = stripeProducts[tier];
    const recurringPlans = (productDefinition.plans.filter(plan => plan.type === "recurring" && plan.isDefault) as RecurringPlanDefinition[]);
    const [prices] = useState<{ year: RecurringPlanDefinition; month?: RecurringPlanDefinition }>(() => ({
        year: recurringPlans.find(plan => plan.interval === "year"),
        month: recurringPlans.find(plan => plan.interval === "month")
    }));

    const planDefinition = prices[billingInterval];
    const priceId = planDefinition?.planId;
    const hasPaymentMethod = paymentMethod?.type;

    const handleCancelDialog = () => {
        closeDialog(false);
    };

    const handleBillingIntervalChange = (newInterval: "month" | "year") => {
        setBillingInterval(newInterval);
    };

    const renderHeader = () => {
        return (
            <StyledDialogTitle>
                <img src={getStaticUrl("/images/beautifulai-logos/beautifulai-checkout-logo.svg")} />
                <div>{t("Complete checkout")}</div>
            </StyledDialogTitle>
        );
    };

    const renderSummarySection = () => {
        return (
            <SummarySection
                billingInterval={billingInterval}
                tier={tier}
                seats={1}
                onBillingIntervalChange={handleBillingIntervalChange}
                showBillingSwitch={true}
                subscription={subscription}
            />
        );
    };

    const availableTrialDays = (): number => {
        try {
            // Only allow trial if user has available days for PRO tier
            return app.user?.availableTrialDays?.[tier] ?? 0;
        } catch {
            return 0;
        }
    };

    const handleSubmit = async () => {
        onUpgradeStart?.();

        closeDialog(true);
        try {
            const progressDialog = ShowDialog(ProgressDialog, { title: t("Upgrading workspace ...") });
            try {
                if (!paymentMethod?.id) {
                    return;
                }

                await upgradeWorkspacePlan({
                    workspaceId: workspaceId,
                    quantity: 1,
                    tier,
                    paymentMethodId: paymentMethod?.id,
                    priceId: priceId,
                    billingDetails: {
                        address: billingAddress,
                        taxId: taxIdValue && taxIdType ? { type: taxIdType, value: taxIdValue } : undefined
                    },
                    promotionCode: undefined
                });

                setPersistentWorkspaceId(workspaceId, "CREATED");

                Analytics.ModifyWorkspacePlan.UpgradeFlowComplete();

                const dialog = ShowDialog(SuccessfulPurchaseDialog, {
                    productDisplayName: productDefinition.productName,
                    callToActionText: t("Continue"),
                    showTrial: (availableTrialDays() > 0),
                    trialExpirationDate: moment().add(availableTrialDays(), "days").format("MMMM Do YYYY"),
                    onCallToActionClick: () => {
                        dialog.close();
                        closeDialog(true);
                        AppController.showAccountPane({ pane: "billing" });
                    }
                });
            } catch (err) {
                const errorMessage = String(err?.message || err).toLowerCase();
                const errorCode = err?.code;

                const isPaymentError = errorMessage.includes("payment") ||
                    errorMessage.includes("card") ||
                    errorMessage.includes("stripe") ||
                    errorCode === "card_declined" ||
                    errorCode === "expired_card" ||
                    errorCode === "insufficient_funds";

                if (isPaymentError) {
                    ShowErrorDialog({
                        title: t("Error processing payment"),
                        message: (<>
                            {t("Sorry, we had a problem processing your payment")}
                            <br />
                            <SmallText>{t("Error: {{message}}", { message: err.message })}</SmallText>
                        </>)
                    });

                    const updated = await ShowDialogAsync(ChangePaymentMethodDialog);
                    if (!updated) {
                        throw new UpgradeFailedError(err as Error);
                    }
                } else {
                    throw new UpgradeFailedError(err as Error);
                }
            } finally {
                progressDialog.close();
            }
        } catch (err) {
            if (err instanceof UpgradeFailedError) {
                ShowErrorDialog({
                    title: t("Error processing payment"),
                    message: (<>
                        {t("Sorry, we had a problem processing your payment")}
                        <br />
                        <SmallText>{t("Error: {{message}}", { message: err.message })}</SmallText>
                    </>)
                });
            } else {
                ShowErrorDialog({
                    title: t("Error upgrading workspace"),
                    message: (<>
                        {t("Sorry, we had a problem upgrading your workspace")}
                        <br />
                        <SmallText>{t("Error: {{message}}", { message: err.message })}</SmallText>
                    </>)
                });
            }
        }
    };

    const renderActions = () => {
        return (
            <StyledDialogActions>
                <Button unframed color={themeColors.mediumGray} onClick={handleCancelDialog}>
                    {t("Cancel")}
                </Button>
                <Button blue disabled={!hasPaymentMethod} onClick={handleSubmit}>
                    {t("Start pro plan")}
                </Button>
            </StyledDialogActions>
        );
    };

    return (
        <BeautifulDialog closeDialog={closeDialog}>
            {renderHeader()}
            <DialogContent>
                {renderSummarySection()}
                <FlexBox left column fillWidth gap={10} padding="20px 0px">
                    <SectionTitle>{t("Payment method")}</SectionTitle>
                    <PaymentMethodSection
                        paymentMethod={paymentMethod}
                        setPaymentMethod={setPaymentMethod} />
                    <FlexBox left column fillWidth>
                        <SectionContent>
                            {t("Your subscription will automatically renew {{interval}}.", {
                                interval: billingInterval === "month" ? t("monthly") : t("annually")
                            })}
                        </SectionContent>
                        <TermsOfUseAndRefundPolicy />
                    </FlexBox>
                </FlexBox>
            </DialogContent>
            {renderActions()}
        </BeautifulDialog>
    );
};

export default WorkspaceController.withInitializedState(BillingController.withState(UpgradeToProWorkspaceDialog));
